/**
 * The `Store` interface (SPEC §10) and `MemoryStore`, the in-memory adapter tests run against.
 *
 * Four operations, async, keyed by string. Deliberately small: the app has one user and a few
 * thousand rows, so an index/query layer would be machinery bought for nothing, and every extra
 * store operation is another thing `LocalStore` (T17) and `RemoteStore` (T18) must both implement
 * identically.
 */

import { PREFIX, SCHEMA_VERSION } from './schema';

export interface StoreEntry<T> {
  key: string;
  value: T;
}

export interface Store {
  get<T>(key: string): Promise<T | undefined>;
  /**
   * Idempotent by construction: writing the same key twice with the same value leaves the store
   * in the same state. Attempts are keyed by attempt id precisely so a mid-write reload cannot
   * double-count a banked review (SPEC §10).
   */
  set<T>(key: string, value: T): Promise<void>;
  /** Every entry whose key starts with `prefix`, ordered by key. Omit the prefix for everything. */
  list<T>(prefix?: string): Promise<StoreEntry<T>[]>;
  delete(key: string): Promise<void>;
  /** Wipe everything. Used by `__harness.reset()`; never reachable from the UI. */
  clear(): Promise<void>;
}

/**
 * Values cross the store boundary by structural copy in both directions.
 *
 * Without this, `MemoryStore` would hand callers a live reference to its own object and a caller
 * mutating a returned `ItemRecord` would silently "persist" without a write — which `LocalStore`
 * would not do. The test adapter has to be strict where the real one is strict for free, or tests
 * pass against behaviour that does not ship.
 */
function copy<T>(value: T): T {
  return structuredClone(value);
}

export class MemoryStore implements Store {
  private readonly map = new Map<string, unknown>();

  async get<T>(key: string): Promise<T | undefined> {
    if (!this.map.has(key)) return undefined;
    return copy(this.map.get(key) as T);
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.map.set(key, copy(value));
  }

  async list<T>(prefix = ''): Promise<StoreEntry<T>[]> {
    const out: StoreEntry<T>[] = [];
    for (const [key, value] of this.map) {
      if (key.startsWith(prefix)) out.push({ key, value: copy(value as T) });
    }
    out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
  }

  async delete(key: string): Promise<void> {
    this.map.delete(key);
  }

  async clear(): Promise<void> {
    this.map.clear();
  }

  /** Test convenience. Not on the interface — no production path needs a synchronous count. */
  size(): number {
    return this.map.size;
  }
}

// ---------------------------------------------------------------------------------------------
// Schema version handling
// ---------------------------------------------------------------------------------------------

const SCHEMA_KEY = `${PREFIX.meta}schemaVersion`;

/**
 * Read the stored schema version, stamping it on a store that has never been written to.
 *
 * Returns the version the data on disk was written with. A migration path is not needed yet —
 * there is exactly one version — but the stamp has to exist from the first write or the first
 * migration will have no way to tell v1 data from unversioned data.
 */
export async function ensureSchemaVersion(store: Store): Promise<number> {
  const found = await store.get<number>(SCHEMA_KEY);
  if (typeof found === 'number') return found;
  await store.set(SCHEMA_KEY, SCHEMA_VERSION);
  return SCHEMA_VERSION;
}
